import { gql } from '@apollo/client'

import { PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES } from '../projects/graphQL'

// ATTRIBUTES
export const TEMPLATE_ATTRIBUTES = gql`
  fragment templateAttributes on Template {
    id
    createdAt
    updatedAt
    archivedAt
    identifierPrefix

    templateType
    templateName
    templateData
    templateCategoryName
    accessProfileTemplate {
      id
      name
    }

    templateResponsesCount
    lastRun
    isPublicChecklist

    schedules {
      id
      title
      projectId
      assignees {
        user {
          firstName
          lastName
        }
      }
      assigner {
        user {
          firstName
          lastName
        }
      }
      scheduledJobs(processed: false) {
        id
      }
    }
  }
`

export const TEMPLATES_LIST_ATTRIBUTES = gql`
  fragment templatesListAttributes on Template {
    id
    createdAt
    updatedAt
    archivedAt

    identifierPrefix

    templateName
    templateCategoryName
    accessProfileTemplate {
      id
      name
    }
  }
`

export const LIBRARY_TEMPLATE_ATTRIBUTES = gql`
  fragment libraryTemplateAttributes on LibraryTemplate {
    id
    createdAt
    updatedAt

    templateType
    templateName
    templateDescription
    templateData

    templateResponsesCount
    lastRun

    schedules {
      id
      title
      projectId
      assignees {
        user {
          firstName
          lastName
        }
      }
      assigner {
        user {
          firstName
          lastName
        }
      }
      scheduledJobs(processed: false) {
        id
      }
    }
  }
`

export const TEMPLATE_RESPONSE_LIST_ATTRIBUTES = gql`
  fragment templateResponseListAttributes on TemplateResponse {
    id
    createdAt
    updatedAt
    completedAt
    archivedAt
    fullIdentifier

    accessProfile {
      id
      name
      owner {
        id
        user {
          firstName
          lastName
        }
      }
      actors {
        adminUser {
          id
          user {
            firstName
            lastName
          }
        }
        role
      }
      defaultRole
      accessProfileTemplate {
        id
        name
      }
    }

    tableBlocks
    platformActions {
      id
      title
      status
    }

    relatedRegisterItem {
      id
      name
      identifier
      fullIdentifier
      register {
        identifierPrefix
        name
      }
    }

    project {
      id
      name
      status
    }

    template {
      ... on Template {
        id
        templateName
        templateType
        accessProfileTemplate {
          id
        }
      }
      ... on LibraryTemplate {
        id
        templateName
        templateType
      }
    }

    author {
      ... on Worker {
        id
        user {
          firstName
          lastName
        }
      }
      ... on AdminUser {
        id
        user {
          firstName
          lastName
        }
      }
    }

    attachments {
      id
      key
      url
    }

    responseSummary {
      blocks {
        total
        completed
        incomplete

        totalWithDesiredState

        inDesiredState
        inNeutralState
        inUndesiredState
      }
    }

    location {
      ...projectLocationWithParentAttributes
    }

    subcontractor {
      id
      company {
        id
        name
      }
    }
  }
  ${PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES}
`

export const TEMPLATE_RESPONSE_ATTRIBUTES = gql`
  fragment templateResponseAttributes on TemplateResponse {
    id
    createdAt
    updatedAt
    completedAt
    archivedAt
    fullIdentifier

    accessProfile {
      id
      name
      owner {
        id
        user {
          firstName
          lastName
        }
      }
      actors {
        adminUser {
          id
          user {
            firstName
            lastName
          }
        }
        role
      }
      defaultRole
      accessProfileTemplate {
        id
        name
      }
    }
    responseData

    relatedRegisterItem {
      id
      name
      identifier
      fullIdentifier
      register {
        identifierPrefix
        name
      }
    }

    project {
      id
      name
      status
      timezone
      integrationConnections {
        integration {
          integrationType
        }
      }
    }

    template {
      ... on Template {
        id
        templateName
        templateData
        accessProfileTemplate {
          id
        }
      }
      ... on LibraryTemplate {
        id
        templateName
        templateData
      }
    }

    author {
      ... on Worker {
        id
        user {
          firstName
          lastName
        }
      }
      ... on AdminUser {
        id
        user {
          firstName
          lastName
        }
      }
    }

    attachments {
      id
      key
      url
    }

    responseSummary {
      blocks {
        total
        completed
        incomplete

        totalWithDesiredState

        inDesiredState
        inNeutralState
        inUndesiredState
      }
    }

    location {
      ...projectLocationWithParentAttributes
    }
  }
  ${PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES}
`

export const TEMPLATE_PUBLIC_RESPONSE_ATTRIBUTES = gql`
  fragment templatePublicResponseAttributes on TemplateResponseEntity {
    id
    createdAt
    updatedAt
    completedAt
    archivedAt

    responseData

    template {
      id
      templateName
      templateData
    }

    attachments {
      id
      key
      url
    }
  }
`

export const TEMPLATE_ENTITY_ATTRIBUTES = gql`
  fragment templateEntityAttributes on TemplateEntity {
    id
    templateName
    templateData
  }
`

export const GET_TEMPLATES = gql`
  query templates_UI($filter: TemplateFilterInput, $sort: TemplateSortInput) {
    templates(filter: $filter, sort: $sort) {
      ...templateAttributes
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const GET_TEMPLATES_SELECT = gql`
  query templatesSelect_UI(
    $filter: TemplateFilterInput
    $sort: TemplateSortInput
  ) {
    templates(filter: $filter, sort: $sort) {
      id
      templateName
      templateCategoryName
      identifierPrefix
    }
  }
`

export const GET_TEMPLATE_ENTITIES = gql`
  query templateEntities_UI(
    $subcontractorId: String!
    $filter: TemplateFilterInput
    $sort: TemplateSortInput
  ) {
    templates_public(
      subcontractorId: $subcontractorId
      filter: $filter
      sort: $sort
    ) {
      ...templateEntityAttributes
    }
  }
  ${TEMPLATE_ENTITY_ATTRIBUTES}
`

export const GET_TEMPLATES_LIST = gql`
  query templatesList_UI($filter: TemplateFilterInput) {
    templates(filter: $filter) {
      ...templatesListAttributes
    }
  }
  ${TEMPLATES_LIST_ATTRIBUTES}
`

export const GET_TEMPLATES_LIST_PAGINATED = gql`
  query templatesListPaginated_UI(
    $filter: TemplateFilterInput
    $pagination: PaginationInput
    $sort: TemplateSortInput
  ) {
    templates(filter: $filter, pagination: $pagination, sort: $sort) {
      ...templatesListAttributes
    }
    templatesAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${TEMPLATES_LIST_ATTRIBUTES}
`
export const GET_COMPANY_TEMPLATES = gql`
  query companyTemplates_UI(
    $filter: TemplateFilterInput
    $pagination: PaginationInput
    $sort: TemplateSortInput
  ) {
    templates(filter: $filter, pagination: $pagination, sort: $sort) {
      id
      updatedAt
      archivedAt

      templateType
      templateName

      templateResponsesCount
      templateCategoryName
    }
    templatesAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_ALL_COMPANY_TEMPLATES = gql`
  query allCompanyTemplates_UI($filter: TemplateFilterInput) {
    templates(filter: $filter) {
      id
      createdAt
      updatedAt
      archivedAt

      templateType
      templateName

      templateResponsesCount
      templateCategoryName
    }
  }
`

export const GET_TEMPLATES_LIST_WITH_PROJECT = gql`
  query templatesListWithProjects_UI($filter: TemplateFilterInput) {
    templates(filter: $filter) {
      id
      default

      templateName
      templateData
      templateCategoryName

      builderId

      projects {
        id
        name
        status
      }
    }
  }
`

export const GET_LIBRARY_TEMPLATES = gql`
  query libraryTemplates_UI($filter: LibraryTemplateFilterInput) {
    libraryTemplates(filter: $filter) {
      ...libraryTemplateAttributes
    }
  }
  ${LIBRARY_TEMPLATE_ATTRIBUTES}
`

export const GET_LIBRARY_TEMPLATES_SELECT = gql`
  query libraryTemplatesSelect_UI($filter: LibraryTemplateFilterInput) {
    libraryTemplates(filter: $filter) {
      id
      templateName
    }
  }
`

export const GET_LIBRARY_TEMPLATE = gql`
  query libraryTemplate_UI($id: String!) {
    libraryTemplate(id: $id) {
      ...libraryTemplateAttributes
    }
  }
  ${LIBRARY_TEMPLATE_ATTRIBUTES}
`

export const GET_TEMPLATE = gql`
  query template_UI($id: String!, $archivedFilter: ArchivedFilter) {
    template(id: $id, archivedFilter: $archivedFilter) {
      ...templateAttributes

      projects {
        id
        name
        status
      }
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const GET_TEMPLATE_FROM_MARKETPLACE = gql`
  query templateFromMarketplace_UI($id: String!) {
    templateFromMarketplace(id: $id) {
      id
      templateName
      templateData
    }
  }
`

export const GET_TEMPLATE_DATA = gql`
  query templateData_UI($id: String!, $archivedFilter: ArchivedFilter) {
    template(id: $id, archivedFilter: $archivedFilter) {
      templateData
    }
  }
`

export const GET_TEMPLATE_AS_WORKER = gql`
  query templateAsWorker_UI($id: String!, $projectId: String!) {
    templateAsWorker(id: $id, projectId: $projectId) {
      ...templateEntityAttributes
    }
  }
  ${TEMPLATE_ENTITY_ATTRIBUTES}
`

export const GET_TEMPLATE_IDENTIFIERS = gql`
  query templateIdentifiers_UI {
    templateIdentifiers
  }
`

export const UPDATE_TEMPLATE = gql`
  mutation updateTemplate_UI($id: String!, $input: UpdateTemplateInput!) {
    updateTemplate(id: $id, input: $input) {
      ...templateAttributes
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const BULK_UPDATE_TEMPLATES = gql`
  mutation bulkUpdateTemplates_UI(
    $ids: [String!]!
    $input: UpdateTemplateInput!
  ) {
    bulkUpdateTemplates(ids: $ids, input: $input) {
      id
    }
  }
`

export const GET_TEMPLATE_RESPONSES = gql`
  query templateResponses_UI(
    $filter: TemplateResponseFilterInput
    $pagination: PaginationInput
    $sort: TemplateResponseSortInput
    $searchString: String
  ) {
    templateResponses(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...templateResponseListAttributes
    }
    templateResponsesAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${TEMPLATE_RESPONSE_LIST_ATTRIBUTES}
`

export const GET_TEMPLATE_RESPONSES_FOR_CSV = gql`
  query templateResponsesForCsv_UI(
    $filter: TemplateResponseFilterInput
    $pagination: PaginationInput
    $sort: TemplateResponseSortInput
    $searchString: String
  ) {
    templateResponses(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`

export const GET_TEMPLATE_RESPONSE = gql`
  query templateResponse_UI($id: String!) {
    templateResponse(id: $id) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`

export const GET_TEMPLATE_RESPONSES_AGGREGATE_STATS = gql`
  query templateResponsesAggregateStats_UI(
    $filter: TemplateResponseFilterInput
  ) {
    templateResponsesAggregateStats(filter: $filter) {
      status
      count
    }
  }
`

export const GET_TEMPLATE_OR_LIBRARY_TEMPLATE = gql`
  query templateOrLibraryTemplate_UI(
    $templateType: TemplateType!
    $projectId: String
  ) {
    templateOrLibraryTemplate(
      templateType: $templateType
      projectId: $projectId
    ) {
      ... on Template {
        ...templateAttributes
      }
      ... on LibraryTemplate {
        ...libraryTemplateAttributes
      }
    }
  }
  ${TEMPLATE_ATTRIBUTES}
  ${LIBRARY_TEMPLATE_ATTRIBUTES}
`

export const GET_TEMPLATE_OR_LIBRARY_TEMPLATE_DATA = gql`
  query templateOrLibraryTemplateData_UI(
    $templateType: TemplateType!
    $projectId: String
  ) {
    templateOrLibraryTemplate(
      templateType: $templateType
      projectId: $projectId
    ) {
      ... on Template {
        id
        templateData
      }
      ... on LibraryTemplate {
        id
        templateData
      }
    }
  }
`

export const CREATE_TEMPLATE_RESPONSE = gql`
  mutation createTemplateResponse_UI($input: CreateTemplateResponseInput!) {
    createTemplateResponse(input: $input) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`

export const CREATE_PUBLIC_TEMPLATE_RESPONSE = gql`
  mutation createPublicTemplateResponse_UI(
    $input: CreatePublicTemplateResponseInput!
  ) {
    createPublicTemplateResponse(input: $input) {
      ...templatePublicResponseAttributes
    }
  }
  ${TEMPLATE_PUBLIC_RESPONSE_ATTRIBUTES}
`

export const UPDATE_PUBLIC_TEMPLATE_RESPONSE = gql`
  mutation updatePublicTemplateResponse_UI(
    $id: String!
    $input: UpdatePublicTemplateResponseInput!
  ) {
    updatePublicTemplateResponse(id: $id, input: $input) {
      ...templatePublicResponseAttributes
    }
  }
  ${TEMPLATE_PUBLIC_RESPONSE_ATTRIBUTES}
`

export const CREATE_LIBRARY_TEMPLATE = gql`
  mutation createLibraryTemplate_UI($input: CreateLibraryTemplateInput!) {
    createLibraryTemplate(input: $input) {
      id
      templateName
    }
  }
`

export const CREATE_COMPANY_TEMPLATE = gql`
  mutation createTemplate_UI($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      ...templateAttributes
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const UPDATE_LIBRARY_TEMPLATE = gql`
  mutation updateLibraryTemplate_UI(
    $id: String!
    $input: UpdateLibraryTemplateInput!
  ) {
    updateLibraryTemplate(id: $id, input: $input) {
      ...libraryTemplateAttributes
    }
  }
  ${LIBRARY_TEMPLATE_ATTRIBUTES}
`

export const UPDATE_TEMPLATE_RESPONSE = gql`
  mutation updateTemplateResponse_UI(
    $id: String!
    $input: UpdateTemplateResponseInput!
  ) {
    updateTemplateResponse(id: $id, input: $input) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`

export const COMPLETE_TEMPLATE_RESPONSE = gql`
  mutation completeTemplateResponse_UI($id: String!) {
    completeTemplateResponse(id: $id) {
      id
    }
  }
`

export const ARCHIVE_TEMPLATE = gql`
  mutation archiveTemplate_UI($id: String!) {
    archiveTemplate(id: $id) {
      ...templateAttributes
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const RESTORE_TEMPLATE = gql`
  mutation restoreTemplate_UI($id: String!) {
    restoreTemplate(id: $id) {
      ...templateAttributes
    }
  }
  ${TEMPLATE_ATTRIBUTES}
`

export const TRANSFER_TEMPLATE = gql`
  mutation transferTemplate_UI(
    $id: String!
    $name: String!
    $builderId: String!
  ) {
    transferTemplate(id: $id, name: $name, builderId: $builderId)
  }
`

export const TRANSFER_TEMPLATES = gql`
  mutation transferTemplates_UI(
    $templates: [TransferTemplateInput!]!
    $builderId: String!
  ) {
    transferTemplates(templates: $templates, builderId: $builderId)
  }
`

export const ARCHIVE_TEMPLATE_RESPONSE = gql`
  mutation archiveTemplateResponse_UI($id: String!) {
    archiveTemplateResponse(id: $id) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`
export const RESTORE_TEMPLATE_RESPONSE = gql`
  mutation restoreTemplateResponse_UI($id: String!) {
    restoreTemplateResponse(id: $id) {
      ...templateResponseAttributes
    }
  }
  ${TEMPLATE_RESPONSE_ATTRIBUTES}
`

export const CREATE_TEMPLATE_RESPONSE_AS_WORKER = gql`
  mutation createTemplateResponseAsWorker_UI(
    $input: CreateTemplateResponseAsWorkerInput!
  ) {
    createTemplateResponseAsWorker(input: $input) {
      id
    }
  }
`

export const UPDATE_TEMPLATE_RESPONSE_AS_WORKER = gql`
  mutation updateTemplateResponseAsWorker_UI(
    $input: UpdateTemplateResponseAsWorkerInput!
  ) {
    updateTemplateResponseAsWorker(input: $input) {
      id
    }
  }
`
export const UPDATE_TEMPLATE_RESPONSE_RELATED_REGISTER_ITEM = gql`
  mutation updateTemplateResponseRelatedRegisterItem_UI(
    $id: String!
    $registerItemId: String
  ) {
    updateTemplateResponseRelatedRegisterItem(
      id: $id
      registerItemId: $registerItemId
    ) {
      id
    }
  }
`

export const CONNECT_PROJECT_TO_TEMPLATE = gql`
  mutation connectProjectToTemplate_UI(
    $projectId: String!
    $templateId: String!
    $input: AssignProjectToTemplateInput!
  ) {
    connectProjectToTemplate(
      projectId: $projectId
      templateId: $templateId
      input: $input
    ) {
      id
    }
  }
`

export const DISCONNECT_PROJECT_FROM_TEMPLATE_TYPE = gql`
  mutation disconnectProjectFromTemplateType_UI(
    $projectId: String!
    $templateType: TemplateType!
  ) {
    disconnectProjectFromTemplateType(
      projectId: $projectId
      templateType: $templateType
    ) {
      id
    }
  }
`

export const BULK_ARCHIVE_TEMPLATES = gql`
  mutation bulkArchiveTemplates_UI($ids: [String!]!) {
    bulkArchiveTemplates(ids: $ids)
  }
`

export const BULK_RESTORE_TEMPLATES = gql`
  mutation bulkRestoreTemplates_UI($ids: [String!]!) {
    bulkRestoreTemplates(ids: $ids)
  }
`
